import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Mail, CalendarPlus, Pencil, Trash2, ChevronDown, ChevronUp, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    adminClients,
    adminCreateClient,
    adminUpdateClient,
    adminDeleteClient,
    adminSendClientEmail,
    adminCreateBooking,
} from "../../lib/api";

const EMPTY = { name: "", email: "", phone: "", company: "", contract_signed: false, delivery_date: "", notes: "" };

const fmtDate = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export const Clients = ({ token }) => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(null); // expanded client_id
    const [mail, setMail] = useState({ subject: "", body: "" });
    const [meeting, setMeeting] = useState({ date: "", time: "", duration_hours: 1, title: "" });
    const [panelBusy, setPanelBusy] = useState(false);

    const load = () => adminClients(token).then(setItems).catch(() => {});
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (editing) {
                await adminUpdateClient(token, editing, form);
                toast.success("Client updated.");
            } else {
                await adminCreateClient(token, form);
                toast.success("Client added.");
            }
            setForm(EMPTY);
            setEditing(null);
            load();
        } catch {
            toast.error("Could not save client.");
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (c) => {
        setEditing(c.client_id);
        setForm({
            name: c.name,
            email: c.email || "",
            phone: c.phone || "",
            company: c.company || "",
            contract_signed: !!c.contract_signed,
            delivery_date: c.delivery_date || "",
            notes: c.notes || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete client ${c.name}?`)) return;
        await adminDeleteClient(token, c.client_id).catch(() => {});
        load();
    };

    const sendMail = async (c) => {
        setPanelBusy(true);
        try {
            await adminSendClientEmail(token, { to: c.email, subject: mail.subject, body: mail.body });
            toast.success(`Email sent to ${c.email}.`);
            setMail({ subject: "", body: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed.");
        } finally {
            setPanelBusy(false);
        }
    };

    const scheduleMeeting = async (c) => {
        setPanelBusy(true);
        try {
            await adminCreateBooking(token, {
                name: c.name,
                email: c.email || "",
                phone: c.phone || "",
                service: "web-dev",
                service_label: meeting.title || `Dev check-in — ${c.name}`,
                date: meeting.date,
                time: meeting.time,
                duration_hours: meeting.duration_hours,
                notes: "",
                location: "virtual",
            });
            toast.success(`Dev meeting booked for ${c.name}${c.email ? " — confirmation emailed with Google Meet link" : ""}.`);
            setMeeting({ date: "", time: "", duration_hours: 1, title: "" });
            load();
        } catch {
            toast.error("Could not schedule meeting.");
        } finally {
            setPanelBusy(false);
        }
    };

    return (
        <section id="panel-clients" className="border border-ink/10 bg-white" data-testid="admin-clients-panel">
            <div className="px-6 py-4 border-b border-ink/10">
                <h2 className="font-display font-medium text-xl">Clients</h2>
                <p className="text-xs text-ink/50 mt-1">
                    Contact details, contract & delivery tracking, related proposals and invoices — plus custom emails
                    and dev meetings for ongoing projects.
                </p>
            </div>

            <form onSubmit={submit} data-testid="client-form" className="p-6 space-y-4 border-b border-ink/10">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="c-name">Name *</Label>
                        <Input id="c-name" data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 rounded-none" />
                    </div>
                    <div>
                        <Label htmlFor="c-company">Company</Label>
                        <Input id="c-company" data-testid="client-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-none" />
                    </div>
                    <div>
                        <Label htmlFor="c-email">Email</Label>
                        <Input id="c-email" data-testid="client-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-none" />
                    </div>
                    <div>
                        <Label htmlFor="c-phone">Phone</Label>
                        <Input id="c-phone" data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-none" />
                    </div>
                    <div>
                        <Label htmlFor="c-delivery">Delivery / due date</Label>
                        <Input id="c-delivery" data-testid="client-delivery-input" type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="mt-1.5 rounded-none" />
                    </div>
                    <label className="flex items-end gap-2.5 pb-2 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="client-contract-check"
                            checked={form.contract_signed}
                            onChange={(e) => setForm({ ...form, contract_signed: e.target.checked })}
                            className="h-4 w-4 accent-[#FF4500]"
                        />
                        <span className="text-sm">Contract signed</span>
                    </label>
                </div>
                <div>
                    <Label htmlFor="c-notes">Notes</Label>
                    <Textarea id="c-notes" data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Project scope, links, anything to remember…" className="mt-1.5 rounded-none" />
                </div>
                <div className="flex gap-3">
                    <Button data-testid="client-save-btn" type="submit" disabled={busy} className="bg-ink hover:bg-worx text-paper rounded-none">
                        {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus className="mr-2" size={16} />}
                        {editing ? "Update client" : "Add client"}
                    </Button>
                    {editing && (
                        <Button type="button" variant="outline" className="rounded-none" onClick={() => { setEditing(null); setForm(EMPTY); }}>
                            Cancel
                        </Button>
                    )}
                </div>
            </form>

            {items.length > 0 && (
                <ul data-testid="clients-list" className="divide-y divide-ink/10">
                    {items.map((c) => {
                        const overdue = c.delivery_date && c.delivery_date < new Date().toISOString().slice(0, 10);
                        const expanded = open === c.client_id;
                        return (
                            <li key={c.client_id} data-testid={`client-${c.client_id.slice(0, 8)}`}>
                                <button
                                    type="button"
                                    data-testid={`client-expand-${c.client_id.slice(0, 8)}`}
                                    onClick={() => setOpen(expanded ? null : c.client_id)}
                                    className="w-full px-6 py-4 flex items-center justify-between gap-3 text-left hover:bg-paper/60 transition-colors"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm">
                                            {c.name}
                                            {c.company && <span className="text-ink/50 font-normal"> · {c.company}</span>}
                                        </p>
                                        <p className="text-xs text-ink/50 truncate">
                                            {[c.email, c.phone].filter(Boolean).join(" · ") || "No contact info"}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            <span className={`text-xs px-2 py-0.5 ${c.contract_signed ? "bg-green-100 text-green-800" : "bg-ink/5 text-ink/50"}`}>
                                                {c.contract_signed ? "Contract signed" : "No contract"}
                                            </span>
                                            {c.delivery_date && (
                                                <span className={`text-xs px-2 py-0.5 ${overdue ? "bg-worx/10 text-worx font-medium" : "bg-ink/5 text-ink/60"}`}>
                                                    Due {fmtDate(c.delivery_date)}{overdue ? " — overdue" : ""}
                                                </span>
                                            )}
                                            <span className="text-xs px-2 py-0.5 bg-ink/5 text-ink/60">
                                                {c.proposals?.length || 0} proposals · {c.invoices?.length || 0} invoices · {c.meetings?.length || 0} upcoming
                                            </span>
                                        </div>
                                    </div>
                                    {expanded ? <ChevronUp size={16} className="text-ink/40 shrink-0" /> : <ChevronDown size={16} className="text-ink/40 shrink-0" />}
                                </button>

                                {expanded && (
                                    <div className="px-6 pb-6 pt-1 space-y-5 border-t border-ink/5 bg-paper/40">
                                        {c.notes && <p className="text-sm text-ink/70 italic">{c.notes}</p>}

                                        <div className="grid sm:grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <p className="text-xs uppercase tracking-[0.2em] text-ink/50 mb-1.5">Proposals</p>
                                                {c.proposals?.length ? (
                                                    <ul className="space-y-1">{c.proposals.map((p) => <li key={p.proposal_id} className="text-ink/70">{p.title} <span className="text-ink/40">({p.status})</span></li>)}</ul>
                                                ) : <p className="text-ink/40 text-xs">None yet</p>}
                                            </div>
                                            <div>
                                                <p className="text-xs uppercase tracking-[0.2em] text-ink/50 mb-1.5">Invoices</p>
                                                {c.invoices?.length ? (
                                                    <ul className="space-y-1">{c.invoices.map((i) => <li key={i.invoice_id} className="text-ink/70">{i.invoice_no} · ${i.amount.toFixed(2)} <span className="text-ink/40">({i.status})</span></li>)}</ul>
                                                ) : <p className="text-ink/40 text-xs">None yet</p>}
                                            </div>
                                            <div>
                                                <p className="text-xs uppercase tracking-[0.2em] text-ink/50 mb-1.5">Upcoming meetings</p>
                                                {c.meetings?.length ? (
                                                    <ul className="space-y-1">{c.meetings.map((m) => <li key={m.booking_id} className="text-ink/70">{m.date} {m.time} <span className="text-ink/40">· {m.service_name}</span></li>)}</ul>
                                                ) : <p className="text-ink/40 text-xs">None scheduled</p>}
                                            </div>
                                        </div>

                                        {c.email && (
                                            <div className="border border-ink/10 bg-white p-4" data-testid={`client-email-composer-${c.client_id.slice(0, 8)}`}>
                                                <p className="text-xs uppercase tracking-[0.2em] text-ink/50 mb-2 flex items-center gap-1.5"><Mail size={12} /> Custom email to {c.email}</p>
                                                <Input data-testid={`client-email-subject-${c.client_id.slice(0, 8)}`} placeholder="Subject" value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} className="rounded-none mb-2" />
                                                <Textarea data-testid={`client-email-body-${c.client_id.slice(0, 8)}`} placeholder="Write your message… (sent with your Consulting WorX branding)" value={mail.body} onChange={(e) => setMail({ ...mail, body: e.target.value })} rows={3} className="rounded-none mb-2" />
                                                <Button type="button" size="sm" data-testid={`client-email-send-${c.client_id.slice(0, 8)}`} disabled={panelBusy || !mail.subject || !mail.body} onClick={() => sendMail(c)} className="bg-ink hover:bg-worx text-paper rounded-none">
                                                    <Send size={13} className="mr-1.5" /> Send email
                                                </Button>
                                            </div>
                                        )}

                                        <div className="border border-ink/10 bg-white p-4" data-testid={`client-meeting-form-${c.client_id.slice(0, 8)}`}>
                                            <p className="text-xs uppercase tracking-[0.2em] text-ink/50 mb-2 flex items-center gap-1.5"><CalendarPlus size={12} /> Schedule dev meeting</p>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                <Input data-testid={`meeting-title-${c.client_id.slice(0, 8)}`} placeholder={`Dev check-in — ${c.name}`} value={meeting.title} onChange={(e) => setMeeting({ ...meeting, title: e.target.value })} className="rounded-none col-span-2" />
                                                <Input data-testid={`meeting-date-${c.client_id.slice(0, 8)}`} type="date" value={meeting.date} onChange={(e) => setMeeting({ ...meeting, date: e.target.value })} className="rounded-none" />
                                                <Input data-testid={`meeting-time-${c.client_id.slice(0, 8)}`} type="time" value={meeting.time} onChange={(e) => setMeeting({ ...meeting, time: e.target.value })} className="rounded-none" />
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <select
                                                    data-testid={`meeting-duration-${c.client_id.slice(0, 8)}`}
                                                    value={meeting.duration_hours}
                                                    onChange={(e) => setMeeting({ ...meeting, duration_hours: parseInt(e.target.value, 10) })}
                                                    className="border border-ink/15 px-2 py-2 text-sm bg-white"
                                                >
                                                    {[1, 2, 3, 4].map((h) => <option key={h} value={h}>{h} hour{h > 1 ? "s" : ""}</option>)}
                                                </select>
                                                <Button type="button" size="sm" data-testid={`meeting-book-${c.client_id.slice(0, 8)}`} disabled={panelBusy || !meeting.date || !meeting.time} onClick={() => scheduleMeeting(c)} className="bg-ink hover:bg-worx text-paper rounded-none">
                                                    <CalendarPlus size={13} className="mr-1.5" /> Book meeting
                                                </Button>
                                                <span className="text-xs text-ink/40">Syncs to Google Calendar{c.email ? " + emails the client" : ""}</span>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 pt-1">
                                            <button data-testid={`client-edit-${c.client_id.slice(0, 8)}`} onClick={() => startEdit(c)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-worx hover:text-worx transition-colors">
                                                <Pencil size={13} /> Edit
                                            </button>
                                            <button data-testid={`client-delete-${c.client_id.slice(0, 8)}`} onClick={() => remove(c)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-worx hover:text-worx transition-colors">
                                                <Trash2 size={13} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
